import React, { useContext, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Box,
  Grid,
  CardMedia,
  CardActionArea,
  Chip,
  FormControl,
  InputLabel,
  CircularProgress
} from '@mui/material';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import {
  Star,
  Image as ImageIcon,
  KeyboardArrowDown,
  Close
} from '@mui/icons-material';
import ApiFetch from '../ApiFetch';
import { LoginInfo } from '../../App';
import Thumb from '../common/Thumb';
import { Spinner } from 'react-bootstrap';

// Define constants for model types and style types
const MODEL_TYPES = [
  { key: 'all', name: 'すべて' },
  { key: 'SD1.5', name: 'SD1.5' },
  { key: 'SDXL', name: 'SDXL' },
  { key: 'SD3', name: 'SD3' },
  { key: 'DALL-E3', name: 'OpenAI' }
];

const STYLE_TYPES = [
  { key: 'all', name: 'すべて' },
  { key: 'illustration', name: 'イラスト' },
  { key: 'semirealism', name: 'セミリアル' },
  { key: 'photorealistic', name: 'フォトリアル' },
  { key: 'common', name: '汎用' }
];

const SAMPLE_MODELS = [
  {
    id: 1,
    i_name: "sd-v1-5",
    name: "Stable Diffusion 1.5",
    img: "sample1.jpg",
    model_type: "SD1.5",
    style: "illustration",
    favorite: 1,
    description: "高品質な画像生成が可能な基本モデル"
  },
  {
    id: 2,
    i_name: "sdxl-1.0",
    name: "SDXL 1.0",
    img: "sample2.jpg",
    model_type: "SDXL",
    style: "semirealism",
    favorite: 0,
    description: "より大きなサイズと高品質な画像生成が可能"
  },
  {
    id: 3,
    i_name: "dall-e-3",
    name: "DALL-E 3",
    img: "sample3.jpg",
    model_type: "OpenAI",
    style: "photorealistic",
    favorite: 0,
    description: "OpenAIによる最新の画像生成モデル"
  },
  {
    id: 4,
    i_name: "dall-e-3",
    name: "DALL-E 3",
    img: "sample3.jpg",
    model_type: "OpenAI",
    style: "photorealistic",
    favorite: 0,
    description: "OpenAIによる最新の画像生成モデル"
  },
  {
    id: 5,
    i_name: "dall-e-3",
    name: "DALL-E 3",
    img: "sample3.jpg",
    model_type: "OpenAI",
    style: "photorealistic",
    favorite: 0,
    description: "OpenAIによる最新の画像生成モデル"
  }
];

// Sample images data
const SAMPLE_IMAGES = {
  "sd-v1-5": [
    { url: "/api/placeholder/800/600", title: "風景画像1" },
    { url: "/api/placeholder/800/600", title: "ポートレート1" },
    { url: "/api/placeholder/800/600", title: "抽象画1" }
  ],
  "sdxl-1.0": [
    { url: "/api/placeholder/800/600", title: "風景画像2" },
    { url: "/api/placeholder/800/600", title: "ポートレート2" }
  ],
  "dall-e-3": [
    { url: "/api/placeholder/800/600", title: "風景画像3" },
    { url: "/api/placeholder/800/600", title: "ポートレート3" }
  ]
};

const FilterHeader = ({
  showFavorites,
  setShowFavorites,
  selectedStyle,
  setSelectedStyle,
  selectedModelType,
  setSelectedModelType,
  searchQuery,
  setSearchQuery
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // モバイル用のフィルターコンテンツ
  const filterContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Button
        fullWidth
        variant={showFavorites ? "contained" : "outlined"}
        onClick={() => setShowFavorites(!showFavorites)}
        startIcon={<Star />}
      >
        お気に入り
      </Button>

      <FormControl fullWidth>
        <InputLabel>画風</InputLabel>
        <Select
          value={selectedStyle}
          onChange={(e) => setSelectedStyle(e.target.value)}
          label="画風"
        >
          {STYLE_TYPES.map(style => (
            <MenuItem key={style.key} value={style.key}>
              {style.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth>
        <InputLabel>モデル種別</InputLabel>
        <Select
          value={selectedModelType}
          onChange={(e) => setSelectedModelType(e.target.value)}
          label="モデル種別"
        >
          {MODEL_TYPES.map(type => (
            <MenuItem key={type.key} value={type.key}>
              {type.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        placeholder="検索"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
    </Box>
  );

  return (
    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6">モデル一覧</Typography>

        {isMobile ? (
          // モバイル用アコーディオンレイアウト
          <Accordion
            sx={{
              boxShadow: 'none',
              '&:before': { display: 'none' },
              bgcolor: 'transparent'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 0,
                '& .MuiAccordionSummary-content': {
                  margin: 0
                }
              }}
            >
              <Typography color="primary">フィルター</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0 }}>
              {filterContent}
            </AccordionDetails>
          </Accordion>
        ) : (
          // デスクトップ用通常レイアウト
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Button
              variant={showFavorites ? "contained" : "outlined"}
              onClick={() => setShowFavorites(!showFavorites)}
              startIcon={<Star />}
            >
              お気に入り
            </Button>

            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>画風</InputLabel>
              <Select
                value={selectedStyle}
                onChange={(e) => setSelectedStyle(e.target.value)}
                label="画風"
              >
                {STYLE_TYPES.map(style => (
                  <MenuItem key={style.key} value={style.key}>
                    {style.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>モデル種別</InputLabel>
              <Select
                value={selectedModelType}
                onChange={(e) => setSelectedModelType(e.target.value)}
                label="モデル種別"
              >
                {MODEL_TYPES.map(type => (
                  <MenuItem key={type.key} value={type.key}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              placeholder="検索"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: { sm: 200 } }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const ModelSelectorDialog = ({ defaultModel = "", setModel = () => { } }) => {
  async function getModelList() {
    return await new ApiFetch().get(ApiFetch.API_PROTOCOL + "//" + ApiFetch.API_HOST + "/v1/sd/modelList/2").then((r) => r.json().then((json) => {
      return json
    }))
  }
  async function getSampleImages(model, modelId) {
    return await new ApiFetch().post(ApiFetch.API_PROTOCOL + "//" + ApiFetch.API_HOST + "/v1/image/modelSamples", { model: model, modelId: modelId }).then((r) => r.json().then((json) => {
      return json
    }))
  }
  // State management
  // ログイン情報
  const { loginInfo, setLoginInfo } = useContext(LoginInfo)
  const [models, setModels] = useState(SAMPLE_MODELS);
  const [open, setOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState('animagineXL40_v40');
  const [dialogSelectedModel, setDialogSelectedModel] = useState('animagineXL40_v40');
  const [showFavorites, setShowFavorites] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState({});
  const [selectedModelType, setSelectedModelType] = useState('all');
  const [selectedStyle, setSelectedStyle] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedModelForGallery, setSelectedModelForGallery] = useState(null);
  const [modelImageSamples, setModelImageSamples] = useState([]);
  const [generateSamplesLoading, setGenerateSamplesLoading] = useState(false)
  const [infoDialogOpen, setInfoDialogOpen] = useState(false)
  const [selectedModelForInfo, setSelectedModelForInfo] = useState(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // 情報ダイアログを表示する関数を追加
  const showInfo = (modelId, event) => {
    event.stopPropagation();
    const model = models.find(m => m.id === modelId);
    if (model) {
      setSelectedModelForInfo(model);
      setInfoDialogOpen(true);
    }
  };

  useEffect(() => {
    getModelList().then(r => {
      if (r.result) {
        setModels(r.data)
      }
    })
  }, [])

  useEffect(() => {
    if (galleryOpen) {
      setGenerateSamplesLoading(true)
      getSampleImages(selectedModelForGallery.i_name, selectedModelForGallery.id).then(r => {
        if (r.result) {
          if (r.data.length > 0) {
            setModelImageSamples([].concat(r.data))
          } else {
            setModelImageSamples([])
          }
        } else {
          setModelImageSamples([])
        }
        setGenerateSamplesLoading(false)
      })
    }
  }, [galleryOpen])

  // Handler functions
  const toggleFavorite = async (modelId, event) => {
    event.stopPropagation();

    // 該当モデルのローディング状態を設定
    setFavoriteLoading(prev => ({ ...prev, [modelId]: true }));

    // 対象のモデルを取得
    const targetModel = models.find(model => model.id === modelId);
    if (!targetModel) {
      setFavoriteLoading(prev => ({ ...prev, [modelId]: false }));
      return;
    }

    try {
      // お気に入りの状態に応じてAPIメソッドを選択
      const method = targetModel.favorite === 1 ? 'delete' : 'post';
      const response = await new ApiFetch()[method](
        `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/favorite/image_ai_model/${modelId}`,
        method === 'post' ? {} : undefined
      );

      const json = await response.json();

      if (json.result) {
        // 成功時：モデルのお気に入り状態を更新
        setModels(prevModels => prevModels.map(model => {
          if (model.id === modelId) {
            return { ...model, favorite: model.favorite === 1 ? 0 : 1 };
          }
          return model;
        }));
      } else {
        // エラー時の処理（必要に応じてエラーメッセージの表示などを追加）
        console.error('Favorite toggle failed:', json);
      }
    } catch (error) {
      console.error('API call failed:', error);
    } finally {
      // ローディング状態を解除
      setFavoriteLoading(prev => ({ ...prev, [modelId]: false }));
    }
  };

  const showGallery = (modelId, event) => {
    event.stopPropagation();
    const model = models.find(m => m.id === modelId);
    if (model) {
      setSelectedModelForGallery(model);
      setGalleryOpen(true);
    }
  };

  // Filter models based on selected criteria
  const filteredModels = models.filter(model => {
    const matchesModelType = selectedModelType === 'all' || model.model_type === selectedModelType;
    const matchesStyle = selectedStyle === 'all' || model.style === selectedStyle;
    const matchesSearch = searchQuery === '' ||
      model.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFavorites = !showFavorites || model.favorite === 1;

    return matchesModelType && matchesStyle && matchesSearch && matchesFavorites;
  });
  const [showDialog, setShowDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");
  function dialog(title,message){
      setDialogTitle(title)
      setDialogMessage(message)
      setShow(true)
  }
  return (
    <>
        <Dialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {dialogTitle}
            </Typography>
            <IconButton onClick={() => setShowDialog(false)}>
              <Close />
            </IconButton>
          </Box>
          <Grid container spacing={2}>
              {dialogMessage}
          </Grid>
        </DialogContent>
      </Dialog>
      {/* Model Selector Button */}
      <Button
        variant="outlined"
        onClick={() => setOpen(true)}
        sx={{
          minWidth: 200,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedModel && (
            <Box
              component="img"
              src={models.find(m => m.i_name === selectedModel)?.sample ? models.find(m => m.i_name === selectedModel)?.sample : "/img/" + models.find(m => m.i_name === selectedModel)?.img}
              alt={models.find(m => m.i_name === selectedModel)?.name}
              sx={{ width: 32, height: 32, borderRadius: 1 }}
            />
          )}
          <Typography sx={{ flex: 1 }}>
            {selectedModel
              ? models.find(m => m.i_name === selectedModel)?.sample ? (models.find(m => m.i_name === selectedModel)?.model_name + models.find(m => m.i_name === selectedModel)?.name).substring(0, 20) : models.find(m => m.i_name === selectedModel)?.name.substring(0, 20)
              : "モデルを選択"}
          </Typography>
        </Box>
        <KeyboardArrowDown />
      </Button>

      {/* Main Model Selection Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogContent sx={{ p: 0, height: '80vh', display: 'flex', flexDirection: 'column' }}>
          {/* Header with Filters */}
          <FilterHeader
            showFavorites={showFavorites}
            setShowFavorites={setShowFavorites}
            selectedStyle={selectedStyle}
            setSelectedStyle={setSelectedStyle}
            selectedModelType={selectedModelType}
            setSelectedModelType={setSelectedModelType}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />

          {/* Model Grid */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <Grid container spacing={2}>
              {filteredModels.map(model => (
                <Grid item xs={6} sm={6} md={4} lg={3} key={model.id}>
                  <Box sx={{
                    position: 'relative',
                    width: '100%',
                    // アスペクト比を1:1（正方形）に維持
                    '&::before': {
                      content: '""',
                      display: 'block',
                      paddingTop: '100%'
                    }
                  }}>
                    <Card
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease-in-out',
                        // 選択状態のスタイリングを強化
                        outline: dialogSelectedModel === model.i_name ? '3px solid #f50057' : 'none',
                        transform: dialogSelectedModel === model.i_name ? 'scale(1.02)' : 'none',
                        boxShadow: dialogSelectedModel === model.i_name
                          ? '0 0 20px rgba(245, 0, 87, 0.25)'
                          : 'none',
                        // ホバー時のスタイリング
                        '&:hover': {
                          transform: dialogSelectedModel === model.i_name
                            ? 'scale(1.02) translateY(-8px)'
                            : 'translateY(-8px)',
                          boxShadow: dialogSelectedModel === model.i_name
                            ? '0 8px 20px rgba(245, 0, 87, 0.3)'
                            : '0 8px 16px rgba(0,0,0,0.2)',
                          '& .MuiCardMedia-root': {
                            transform: 'scale(1.05)',
                          },
                          '& .model-info-overlay': {
                            opacity: 1,
                            transform: 'translateY(0)',
                          }
                        },
                        // 選択状態とホバーの組み合わせ時のアニメーション設定
                        '&:hover::after': dialogSelectedModel === model.i_name ? {
                          content: '""',
                          position: 'absolute',
                          top: -3,
                          left: -3,
                          right: -3,
                          bottom: -3,
                          border: '3px solid #f50057',
                          borderRadius: '4px',
                          animation: 'pulse 2s infinite',
                          pointerEvents: 'none'
                        } : {},
                        '@keyframes pulse': {
                          '0%': {
                            opacity: 1,
                            transform: 'scale(1)',
                          },
                          '50%': {
                            opacity: 0.5,
                            transform: 'scale(1.02)',
                          },
                          '100%': {
                            opacity: 1,
                            transform: 'scale(1)',
                          }
                        }
                      }}
                      onClick={() => setDialogSelectedModel(model.i_name)}
                    >
                      <CardActionArea sx={{ overflow: 'hidden' }}>
                        <CardMedia
                          component="img"
                          sx={{
                            height: '100%',
                            transition: 'transform 0.3s ease-in-out',
                            objectFit: 'cover'
                          }}
                          image={model.sample ? model.sample : "/img/" + model.img}
                          alt={model.name}
                        />
                        {/* モデルタイプのChip - SD1.5の場合は背景色を不透明に */}
                        <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
                          <Chip
                            label={model.model_type}
                            color={model.model_type === 'SD1.5' ? 'default' : 'primary'}
                            size="small"
                            sx={{
                              transition: 'transform 0.2s ease',
                              backgroundColor: model.model_type === 'SD1.5' ? 'rgba(128, 128, 128, 0.95)' : undefined,
                              color: model.model_type === 'SD1.5' ? 'white' : undefined,
                              '&:hover': {
                                transform: 'scale(1.05)',
                              }
                            }}
                          />
                        </Box>

                        {/* 操作ボタン群 - 配置を横並びに変更 */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            display: 'flex',
                            gap: isMobile ? 0 : 1,
                            alignItems: 'center',
                          }}
                        >
                          {/* 情報ボタン - 一番左に配置 */}
                          <IconButton
                            onClick={(e) => showInfo(model.id, e)}
                            sx={{
                              bgcolor: 'info.main',
                              color: 'white',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: 'info.dark',
                                transform: 'scale(1.1)',
                              },
                              padding: '6px',
                              '& .MuiSvgIcon-root': {
                                fontSize: '1.2rem',
                              }
                            }}
                          >
                            <InfoIcon />
                          </IconButton>

                          {/* サンプル画像表示ボタン - 左側に配置 */}
                          <IconButton
                            onClick={(e) => showGallery(model.id, e)}
                            sx={{
                              bgcolor: 'primary.main',
                              color: 'white',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: 'primary.dark',
                                transform: 'scale(1.1)',
                              },
                              padding: '6px', // ボタンサイズを少し小さく調整
                              '& .MuiSvgIcon-root': {
                                fontSize: '1.2rem', // アイコンサイズも調整
                              }
                            }}
                          >
                            <ImageIcon />
                          </IconButton>

                          {/* お気に入りボタン - 右側に配置 */}
                          <IconButton
                            onClick={(e) => toggleFavorite(model.id, e)}
                            disabled={favoriteLoading[model.id]}
                            sx={{
                              color: model.favorite === 1 ? 'warning.main' : 'action.disabled',
                              bgcolor: 'background.paper',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                transform: favoriteLoading[model.id] ? 'none' : 'scale(1.1)',
                                bgcolor: 'background.paper',
                              },
                              padding: '6px',
                              '& .MuiSvgIcon-root': {
                                fontSize: '1.2rem',
                              }
                            }}
                          >
                            {favoriteLoading[model.id] ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Star />
                            )}
                          </IconButton>
                        </Box>

                        {/* 情報オーバーレイ */}
                        <Box
                          className="model-info-overlay"
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            p: 2,
                            background: 'linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0.7))',
                            color: 'white',
                            transition: 'all 0.3s ease-in-out',
                            opacity: 0,
                            transform: 'translateY(20px)',
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {model.name}
                          </Typography>
                          <Typography variant="body2" sx={{
                            opacity: 0.9,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}>
                            {model.description}
                          </Typography>
                        </Box>
                      </CardActionArea>
                    </Card>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Dialog Actions */}
          <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
            <Button onClick={() => setOpen(false)} variant="outlined">
              閉じる
            </Button>
            {dialogSelectedModel && (
              <Button
                onClick={() => {
                  setSelectedModel(dialogSelectedModel);
                  setModel(dialogSelectedModel)
                  setOpen(false);
                }}
                variant="contained"
              >
                選択
              </Button>
            )}
          </DialogActions>
        </DialogContent>
      </Dialog>

      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* ヘッダー部分 */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                モデル情報
              </Typography>
              <IconButton onClick={() => setInfoDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>

            {/* モデル情報の表示 */}
            {selectedModelForInfo && (
              <>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box
                    component="img"
                    src={selectedModelForInfo.sample ? selectedModelForInfo.sample : "/img/" + selectedModelForInfo.img}
                    alt={selectedModelForInfo.name}
                    sx={{ width: 80, height: 80, borderRadius: 1, objectFit: 'cover' }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {selectedModelForInfo.name}
                    </Typography>
                    <Chip
                      label={selectedModelForInfo.model_type}
                      color={selectedModelForInfo.model_type === 'SD1.5' ? 'default' : 'primary'}
                      size="small"
                      sx={{
                        backgroundColor: selectedModelForInfo.model_type === 'SD1.5' ? 'rgba(128, 128, 128, 0.95)' : undefined,
                        color: selectedModelForInfo.model_type === 'SD1.5' ? 'white' : undefined,
                      }}
                    />
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    説明
                  </Typography>
                  <Typography variant="body1">
                    {selectedModelForInfo.description}
                  </Typography>
                </Box>

                {/* 追加のモデル情報があれば表示 */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    画風
                  </Typography>
                  <Typography variant="body1">
                    {STYLE_TYPES.find(style => style.key === selectedModelForInfo.style)?.name}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* Gallery Dialog */}
      <Dialog
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {selectedModelForGallery?.name} - サンプル画像
            </Typography>
            <IconButton onClick={() => setGalleryOpen(false)}>
              <Close />
            </IconButton>
          </Box>
          <Grid container spacing={2}>
            {generateSamplesLoading ? <Spinner size='xl' /> : (
              modelImageSamples.map((data, i) => {
                if (data.mute === 1 || (i > 0 && modelImageSamples[i - 1].user_id === data.user_id) || (data.user_id !== loginInfo.userId && data.search_ban === 1)) {
                  return <span data-no={i} className="lazy" key={i}></span>
                }
                return <Thumb data={data} i={i} dialog={dialog} key={i} list={modelImageSamples.filter(d => d.mute === 0)} listName={"samples"} />
              })
            )}
            {/*selectedModelForGallery && modelImageSamples?.map((image, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box sx={{ position: 'relative' }}>
                  <img
                    src={image.url}
                    alt={image.title}
                    style={{ width: '100%', height: 'auto', borderRadius: 4 }}
                  />
                  <Typography
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      p: 1,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      color: 'white'
                    }}
                  >
                    {image.title}
                  </Typography>
                </Box>
              </Grid>
            ))*/}
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ModelSelectorDialog;